if (!customElements.get('quantity-form')) {
  customElements.define(
    'quantity-form',
    class QuantityForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantId = this.form.querySelector('[name=id]').value;
        this.form.querySelector('[name=id]').disabled = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.quantityInput = this.form.querySelector('input[name="quantity"]');
        this.quantityWrapper = this.querySelector('.quantity-selector');

        //console.log('cart', this.cart);

        // Use global debounce function
        this.debouncedUpdateCart = debounce(() => {
          const qty = parseInt(this.quantityInput.value || 0);
          this.updateCartWithQuantity(qty);
        }, 500);

        if (this.quantityWrapper) {
          this.addButton = this.quantityWrapper.querySelector('.plus');
          this.removeButton = this.quantityWrapper.querySelector('.minus');
          this.qtyInput = this.quantityWrapper.querySelector('.qty-input');
          this.maxQuantity = parseInt(this.quantityInput.getAttribute('max')) || Infinity;
        }

        if (this.quantityWrapper) {
          this.addButton.addEventListener('click', () => {
            if (this.addButton.getAttribute('aria-disabled') === 'true') return;

            const currentQty = parseInt(this.quantityInput.value || 0);
            const newQty = Math.min(Math.max(currentQty + 1, 0), this.maxQuantity);
            this.quantityInput.value = newQty;
            this.updateButtonStates(newQty);
            this.debouncedUpdateCart();
          });

          this.removeButton.addEventListener('click', () => {
            if (this.removeButton.getAttribute('aria-disabled') === 'true') return;

            const currentQty = parseInt(this.quantityInput.value || 0);
            const newQty = Math.max(currentQty - 1, 0);
            this.quantityInput.value = newQty;
            this.updateButtonStates(newQty);
            this.debouncedUpdateCart();
          });
        }

        this.form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          const formData = new FormData(this.form);
          const quantity = parseInt(formData.get('quantity') || 1);
        });

        // 🔁 Listen for global cart updates
        document.addEventListener('cartQuantityUpdate', (event) => {
          const eventId = event.detail?.id.toString();
          if (eventId && eventId === this.variantId) {
            this.syncQuantityWithCart(event.detail?.quantity);
          }
        });

        this.classList.remove('product-quantity-form--loading');
      }

      async syncQuantityWithCart(quantity) {
        if (quantity === undefined) {
          this.dataset.itemInCart = 'false';
          return;
        };

        try {
          if (quantity) {
            this.quantityInput.value = quantity;
          }

          if (this.quantityWrapper) {
            if (quantity > 0) {
              this.quantityWrapper.classList.add('show');
            } else {
              this.quantityWrapper.classList.remove('show');
              this.quantityInput.value = 0;
              this.dataset.itemInCart = 'false';
            }
            this.updateButtonStates(quantity);
          }
        } catch (error) {
          console.error('Failed to sync quantity with cart:', error);
        }
      }

      async updateCartWithQuantity(quantity) {
        if (quantity > 0) {
          this.quantityWrapper.classList.add('show');
        } else {
          this.quantityWrapper.classList.remove('show');
          this.quantityInput.value = 0;
        }

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        try {
          const config = fetchConfig();
          const itemInCart = this.dataset.itemInCart;
          const apiUrl = itemInCart === 'true' ? '/cart/change.js' : '/cart/add.js';

          const body = {
            id: this.variantId,
            quantity: quantity
          };

          // Set sections to render for cart add
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);

          const response = await fetch(apiUrl, {
            ...config,
            body: JSON.stringify(body)
          });

          // Handle response
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error ${response.status}: ${errorText}`);
          }

          const cartData = await response.json();
          window.cartData = cartData; // ✅ Update global cart cache
          this.quantityInput.value = quantity;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quantity-form',
            id: this.variantId
          });

          if (cartData.sections) {
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => setTimeout(() => this.cart.renderContents(cartData), 0),
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(cartData);
            }
          }

          const allVariantIdQuantityInputs = document.querySelectorAll(`[data-variant-id="${this.variantId}"]`);
          allVariantIdQuantityInputs.forEach((input) => {
            input.value = quantity;
            input.dataset.cartQuantity = quantity;
          });
        } catch (error) {
          console.error('Cart update error:', error);
          this.handleErrorMessage('Could not update cart item. Try again.');
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        }

        if (quantity > 0) {
          this.dataset.itemInCart = 'true';
        } else {
          this.dataset.itemInCart = 'false';
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      updateButtonStates(quantity) {
        if (!this.quantityWrapper) return;

        const isAtMax = quantity >= this.maxQuantity;
        const isAtMin = quantity <= 0;

        this.addButton.setAttribute('aria-disabled', isAtMax);
        this.addButton.classList.toggle('disabled', isAtMax);
        this.removeButton.setAttribute('aria-disabled', isAtMin);
        this.removeButton.classList.toggle('disabled', isAtMin);
      }
    }
  );
}
